import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, ArrowRight, ArrowLeft, Loader2, Check, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import ResetAssessment from "@/components/ResetAssessment";

const LEVEL_STYLE = {
  MASTERED: "text-tc-bull bg-tc-bull/10", PROFICIENT: "text-tc-coach bg-tc-coach/10",
  DEVELOPING: "text-tc-xp bg-tc-xp/10", NEEDS_EVIDENCE: "text-tc-sub bg-white/5", NOT_ASSESSED: "text-tc-muted bg-white/5",
};

export default function Assess() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("intro");
  const [qs, setQs] = useState([]); const [ans, setAns] = useState({});
  const [busy, setBusy] = useState(false); const [res, setRes] = useState(null);
  const [profile, setProfile] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const DONT_KNOW = -1;

  useEffect(() => { api.get("/competency/profile").then((r) => setProfile(r.data)).catch(() => {}); }, []);

  const leave = () => { setLeaving(false); setAns({}); setQs([]); setPhase("intro"); };

  const start = async () => { setBusy(true); try { const { data } = await api.post("/competency/assess/start", {}); setQs(data.questions); setPhase("quiz"); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const submit = async () => {
    if (Object.keys(ans).length < qs.length) { toast.error("Answer every question."); return; }
    setBusy(true);
    try { const { data } = await api.post("/competency/assess/submit", { answers: ans }); setRes(data); setProfile(data.profile); setPhase("result"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-5" data-testid="assess">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-tc-mastery/15 border border-tc-mastery/30 text-tc-mastery flex items-center justify-center"><Gauge className="w-5 h-5" /></div>
        <div><h1 className="font-heading text-2xl font-bold">Assess My Skills</h1><div className="text-[11px] font-mono text-tc-muted">Demonstrate competency · skip what you already know</div></div>
      </div>

      {profile && phase !== "quiz" && (
        <div className="tc-card p-5" data-testid="competency-profile">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach mb-3">Your Edge · Competency</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {profile.domains.map((d) => (
              <div key={d.domain} className="flex items-center justify-between text-[13px]">
                <span className="text-tc-sub">{d.domain}</span>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${LEVEL_STYLE[d.level]}`} data-testid={`comp-${d.domain.replace(/[^a-z]/gi, "").toLowerCase()}`}>{d.level.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {phase === "intro" && (
        <div className="tc-card p-6 text-center">
          <p className="text-tc-sub">Answer a short, unfamiliar assessment. We measure competency from your answers — never from your age or word. Experienced users can test out of beginner missions.</p>
          <button data-testid="assess-start-btn" onClick={start} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-tc-mastery text-tc-bg font-bold px-6 py-3 hover:bg-tc-mastery/90 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Begin Assessment <ArrowRight className="w-4 h-4" /></button>
        </div>
      )}
      {phase === "intro" && <ResetAssessment onDone={() => api.get("/competency/profile").then((r) => setProfile(r.data)).catch(() => {})} />}

      {phase === "quiz" && (
        <div className="space-y-3" data-testid="assess-quiz">
          <button data-testid="assess-exit-btn" onClick={() => setLeaving(true)} className="text-sm text-tc-muted hover:text-tc-txt flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Exit assessment</button>
          {qs.map((q, qi) => (
            <div key={q.id} className="tc-card p-4">
              <div className="text-[11px] font-mono text-tc-muted">{q.domain}</div>
              <div className="text-sm font-medium mt-1 break-words">{qi + 1}. {q.q}</div>
              <div className="grid gap-2 mt-2">
                {q.options.map((o, oi) => (
                  <button key={oi} data-testid={`q-${q.id}-opt-${oi}`} onClick={() => setAns({ ...ans, [q.id]: oi })}
                    className={`text-left px-3 py-2 rounded-lg border text-sm break-words ${ans[q.id] === oi ? "border-tc-coach bg-tc-coach/10 text-tc-coach" : "border-white/10 text-tc-sub hover:border-white/30"}`}>{o}</button>
                ))}
                <button data-testid={`q-${q.id}-dont-know`} onClick={() => setAns({ ...ans, [q.id]: DONT_KNOW })}
                  className={`text-left px-3 py-2 rounded-lg border border-dashed text-sm ${ans[q.id] === DONT_KNOW ? "border-tc-xp bg-tc-xp/10 text-tc-xp" : "border-white/15 text-tc-muted hover:border-white/30"}`}>
                  <HelpCircle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />I don't know
                </button>
              </div>
            </div>
          ))}
          <button data-testid="assess-submit-btn" onClick={submit} disabled={busy} className="w-full flex items-center justify-center gap-2 rounded-lg bg-tc-mastery text-tc-bg font-bold py-3 hover:bg-tc-mastery/90 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Submit ({Object.keys(ans).length}/{qs.length})</button>
        </div>
      )}

      {phase === "result" && res && (
        <div className="tc-card p-6 text-center space-y-2" data-testid="assess-result">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Assessment Complete</div>
          <div className="font-heading text-4xl font-extrabold">{res.correct}<span className="text-tc-muted text-xl">/{res.total}</span></div>
          <div className="flex justify-center gap-4 text-[12px] font-mono" data-testid="assess-breakdown">
            <span className="text-tc-bull" data-testid="assess-correct-count">{res.correct} correct</span>
            <span className="text-tc-bear" data-testid="assess-wrong-count">{res.wrong ?? 0} wrong</span>
            <span className="text-tc-xp" data-testid="assess-dont-know-count">{res.dont_know ?? 0} didn't know</span>
          </div>
          <p className="text-sm text-tc-sub max-w-lg mx-auto" data-testid="assess-recommendation">{res.recommendation}</p>
          <div className="flex justify-center gap-3 pt-2">
            <button onClick={() => navigate("/training")} className="rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-sm hover:bg-white/10">Trader Path</button>
            <button data-testid="assess-analyze-btn" onClick={() => navigate("/co-analysis")} className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2.5 text-sm hover:bg-tc-coach/90">Analyze a Market</button>
          </div>
        </div>
      )}

      {leaving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-testid="assess-leave-confirm">
          <div className="absolute inset-0 bg-black/70" onClick={() => setLeaving(false)} />
          <div className="relative tc-card p-6 max-w-sm w-full space-y-4">
            <h3 className="font-heading text-lg font-bold">Leave? Progress on this attempt won't be saved</h3>
            <div className="flex gap-3">
              <button data-testid="assess-leave-yes" onClick={leave} className="flex-1 rounded-lg bg-tc-bear/15 border border-tc-bear/40 text-tc-bear font-semibold py-2.5 text-sm hover:bg-tc-bear/25">Leave</button>
              <button data-testid="assess-leave-no" onClick={() => setLeaving(false)} className="flex-1 rounded-lg bg-white/5 border border-white/10 text-tc-sub font-semibold py-2.5 text-sm hover:border-white/30">Stay</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
